import React, { FC, useState } from 'react';
import { Button, InputNumber } from 'antd';

import {
  DeleteOutlined,
  EditOutlined,
  MailOutlined,
  MobileOutlined,
  PhoneOutlined,
  PlusOutlined,
} from '@ant-design/icons';
import { TaxRate } from '../../dto/Tax';
import TaxRateEditModal, { useTaxRateEditModal } from './TaxRateEditModal';
import { Utils } from '../../common/Utils';

type Props = {
  mode: 'add' | 'edit';
  value?: TaxRate[];
  onChange?: (value?: TaxRate[]) => void;
};

export const TaxRateItem: FC<{
  value?: TaxRate;
  onChange?: (value?: TaxRate) => void;
}> = ({ value, onChange }) => {
  const modal = useTaxRateEditModal();
  return (
    <>
      <div
        className="border rounded border-solid border-gray-200 p-2 px-4 relative cursor-pointer hover:border-primary duration-300"
        onClick={() =>
          modal.current
            ?.open(value)
            .then((v) => onChange?.(v))
            .catch((e) => {})
        }
      >
        <div className="flex justify-between items-center max-w-[300px]">
          <div>
            From {value?.date === '1970-01-01' ? 'Begining' : value?.date}
          </div>
          <div>{Utils.formatDecimal(value?.rate ?? 0, 4)}%</div>
        </div>
        <div className="absolute top-0 right-0">
          <Button type="link" shape="circle">
            <EditOutlined />
          </Button>
          <Button
            type="link"
            shape="circle"
            danger={true}
            disabled={value?.date === '1970-01-01'}
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              onChange?.(undefined);
            }}
          >
            <DeleteOutlined />
          </Button>
        </div>
      </div>
      <TaxRateEditModal ref={modal} />
    </>
  );
};

export const TaxRatesInput: FC<Props> = ({ mode, value, onChange }) => {
  const modal = useTaxRateEditModal();
  if (mode === 'add') {
    return (
      <InputNumber
        precision={4}
        style={{ width: '100%' }}
        addonAfter="%"
        value={value ? (value[0].rate ?? 0) / 10000 : undefined}
        onChange={(v) =>
          onChange?.(
            v
              ? [{ date: '1970-01-01', rate: Math.round(v * 10000) }]
              : undefined,
          )
        }
      />
    );
  }
  return (
    <div className="flex flex-col gap-2">
      {value?.map((v: TaxRate, i) => (
        <TaxRateItem
          key={i}
          value={v}
          onChange={(v) => {
            const newVal: TaxRate[] = value
              ?.map((val, idx) => (idx === i ? v : val))
              .filter((val) => !!val) as any;
            onChange?.(newVal.length === 0 ? undefined : newVal);
          }}
        />
      ))}
      <Button
        icon={<PlusOutlined />}
        type={'dashed'}
        onClick={() =>
          modal.current
            ?.open()
            .then((val) => onChange?.([...(value ?? []), val]))
            .catch(() => {})
        }
      >
        Add Tax Rate
      </Button>
      <TaxRateEditModal ref={modal} />
    </div>
  );
};
